import React from 'react';
import { Ellipsis, Folder, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent } from '../atoms/card';
import { Button } from '../atoms/button';
import { ProjectCardProps } from '../../types/ProjectsList.types';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '../atoms/dropdown-menu';
import { cn } from '@ideoz/ui';
import { formatRelativeDate } from '../../utils/dateFormatting';

/**
 * ProjectCard molecule component
 *
 * Displays either a "New Project" button card or an existing project card
 * with name, menu, and hover effects.
 *
 * **Two Variants:**
 * 1. New Project: Simple button card with "+" icon and text
 * 2. Existing Project: Card with project name and optional delete menu
 *
 * @example
 * ```tsx
 * // New project button variant
 * <ProjectCard
 *   newProject
 *   newProjectText="+ New Design Project"
 *   onClick={handleNewProject}
 * />
 *
 * // Existing project variant
 * <ProjectCard
 *   project={{ id: '1', name: 'My Project' }}
 *   onClick={() => openProject('1')}
 *   onDelete={() => deleteProject('1')}
 * />
 * ```
 */

export const ProjectCard: React.FC<ProjectCardProps> = ({
  newProject = false,
  project,
  newProjectText = 'New project',
  onClick,
  onDelete,
  className = '',
  testId = 'project-card',
}) => {
  // New Project variant
  if (newProject) {
    return (
      <Card
        onClick={onClick}
        ata-testid={`${testId}-new`}
        className={cn(
          'group cursor-pointer border border-dashed min-h-48 rounded-3xl',
          'hover:border-primary hover:bg-blue-light-100/20 transition-all duration-200'
        )}
      >
        <CardContent className="p-6 flex flex-col items-center justify-center h-full min-h-[180px]">
          <div
            data-testid={`${testId}-new-button`}
            className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors"
          >
            <Plus className="w-6 h-6 text-primary" />
          </div>
          <h3 className="font-medium lg:font-semibold text-lg text-card-foreground">
            {newProjectText}
          </h3>
          <p className="text-sm text-muted-foreground text-center mt-2">
            Create a new project to get started
          </p>
        </CardContent>
      </Card>
    );
  }

  // Existing Project variant
  if (!project) {
    console.warn('ProjectCard: project prop is required when newProject is false');
    return null;
  }

  return (
    <Card
      onClick={onClick}
      data-testid={`${testId}-${project.id}`}
      className="group hover:shadow-sm transition-all duration-200 cursor-pointer border h-full min-h-48 rounded-3xl"
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
            <Folder className="w-6 h-6 text-primary" />
          </div>
          {onDelete && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="!p-0 h-8 w-8 rounded-md hover:bg-blue-light-50 hover:text-blue-light-600 text-primary"
                  aria-label="Project options"
                  data-testid={`${testId}-menu-trigger`}
                >
                  <Ellipsis size={18} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className={`border p-0 shadow-md ${className}`}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  className="p-2 w-full text-start cursor-pointer text-xs flex gap-2 items-start bg-primary-600 text-white hover:bg-primary-700"
                  data-testid={`${testId}-delete`}
                  data-track="delete_project"
                >
                  <Trash2 size={14} strokeWidth={2} />
                  <span>Delete project</span>
                </button>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        <h3
          className="font-medium lg:font-semibold text-lg mb-2 text-card-foreground line-clamp-1"
          data-testid={`${testId}-name`}
        >
          {project.name}
        </h3>
        <p className="text-sm text-muted-foreground">
          {project?.contentUpdatedAt ? `Updated ${formatRelativeDate(project.contentUpdatedAt)}` : ''}
        </p>
      </CardContent>
    </Card>
  );
};

ProjectCard.displayName = 'ProjectCard';
